import { useEffect, useRef, useState } from "react";
import { X, Download, Copy, Check } from "lucide-react";

const S = 1080;

function drawSpaced(ctx, text, cx, y, spacing) {
  const widths = [...text].map((ch) => ctx.measureText(ch).width);
  const total = widths.reduce((a, w) => a + w + spacing, -spacing);
  let x = cx - total / 2;
  [...text].forEach((ch, i) => {
    ctx.fillText(ch, x, y);
    x += widths[i] + spacing;
  });
}

async function drawBadge(canvas, club, rank, total) {
  await Promise.all([
    document.fonts.load("400 300px Anton"),
    document.fonts.load("500 30px 'IBM Plex Mono'"),
  ]);
  const logo = await new Promise((res) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => res(null);
    img.src = "/brand/nr-mark-white.png";
  });
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, S, S);
  ctx.fillStyle = "#0A0A0A";
  ctx.fillRect(0, 0, S, S);
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = 4;
  ctx.strokeRect(44, 44, S - 88, S - 88);
  ctx.lineWidth = 1;
  ctx.strokeStyle = "rgba(255,255,255,0.25)";
  ctx.strokeRect(60, 60, S - 120, S - 120);

  if (logo) {
    const h = 84;
    const w = (logo.width / logo.height) * h;
    ctx.drawImage(logo, S / 2 - w / 2, 116, w, h);
  }

  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = "500 26px 'IBM Plex Mono'";
  ctx.textBaseline = "alphabetic";
  drawSpaced(ctx, "THE 2026 LIST · NINJA RUN CLUB", S / 2, 268, 6);

  ctx.fillStyle = "#FFFFFF";
  ctx.font = "400 300px Anton";
  ctx.textAlign = "center";
  ctx.fillText(`#${rank}`, S / 2, 560);
  ctx.textAlign = "left";

  ctx.font = "400 64px Anton";
  ctx.textAlign = "center";
  ctx.fillText("BEST RUN CLUB IN AMERICA", S / 2, 650);
  ctx.textAlign = "left";

  const name = club.name.toUpperCase();
  let size = 78;
  ctx.font = `400 ${size}px Anton`;
  while (ctx.measureText(name).width > 900 && size > 34) {
    size -= 4;
    ctx.font = `400 ${size}px Anton`;
  }
  ctx.textAlign = "center";
  ctx.fillText(name, S / 2, 776);
  ctx.textAlign = "left";

  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = "500 26px 'IBM Plex Mono'";
  drawSpaced(ctx, `${club.city}, ${club.state}`.toUpperCase(), S / 2, 828, 5);

  const barW = 560;
  const barX = S / 2 - barW / 2;
  ctx.fillStyle = "rgba(255,255,255,0.15)";
  ctx.fillRect(barX, 862, barW, 8);
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(barX, 862, barW * (club.guide.score / 100), 8);

  ctx.fillStyle = "rgba(255,255,255,0.85)";
  drawSpaced(ctx, `SCORE ${club.guide.score}/100 · RANKED ${rank} OF ${total}`, S / 2, 916, 5);

  ctx.fillStyle = "rgba(255,255,255,0.55)";
  drawSpaced(ctx, "NINJARUNCLUB.COM/RANKED", S / 2, 986, 7);
}

export const RankBadge = ({ club, rank, total, onClose }) => {
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [ready, setReady] = useState(false);

  const caption = `Ranked #${rank} of ${total} on the 2026 Best Run Clubs in America list — scored ${club.guide.score}/100 on pace honesty, no-drop mechanics, and first-timer experience. The full list: https://ninjarunclub.com/ranked`;

  useEffect(() => {
    setReady(false);
    if (canvasRef.current) drawBadge(canvasRef.current, club, rank, total).then(() => setReady(true));
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [club, rank, total, onClose]);

  const download = () => {
    canvasRef.current.toBlob((blob) => {
      if (!blob) return;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `nrc-rank-badge-${club.slug}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    }, "image/png");
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = caption;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="rank-badge-modal" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="bg-night border border-white/15 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono-label text-white/40">SHAREABLE RANK BADGE</p>
            <p className="font-display text-2xl mt-1">{club.name.toUpperCase()}</p>
          </div>
          <button data-testid="rank-badge-close" onClick={onClose} className="text-white/50 hover:text-white transition-colors duration-150 p-1">
            <X size={18} />
          </button>
        </div>
        <canvas ref={canvasRef} width={S} height={S} data-testid="rank-badge-canvas" className="w-full border border-white/10 mt-5" />
        <p className="text-white/40 text-xs mt-3">1080×1080 — sized for Instagram. Tag the list when you post it.</p>
        <div className="flex gap-3 mt-4">
          <button data-testid="rank-badge-download" onClick={download} disabled={!ready} className="btn-invert flex-1 flex items-center justify-center gap-2 bg-white text-ink font-mono-label px-4 py-3.5 border border-white hover:bg-transparent hover:text-white disabled:opacity-50">
            <Download size={14} /> {ready ? "DOWNLOAD PNG" : "RENDERING..."}
          </button>
          <button data-testid="rank-badge-copy-caption" onClick={copyCaption} className="flex-1 flex items-center justify-center gap-2 font-mono-label px-4 py-3.5 border border-white/25 text-white/80 hover:border-white hover:text-white transition-colors duration-200">
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "COPIED" : "COPY CAPTION"}
          </button>
        </div>
      </div>
    </div>
  );
};
